// src/features/expenses/pages/ExpensesPage.tsx
// ENHANCED UI/UX WITH CATEGORY MANAGEMENT, CELEBRATIONS & PROPER ERROR HANDLING
// ✅ NEW: Payee bank details for non‑cash payment methods (stored in metadata)

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import * as expensesApi from '../services/expensesApi';
import * as bankingApi from '../../banking/services/bankingApi';
import type { Expense, ExpenseCategory } from '../services/expensesApi';
import type { BankAccount } from '../../banking/services/bankingApi';
import {
    Receipt, Plus, Save, ArrowLeft, CheckCircle2, XCircle,
    Clock, Search, Trash2, Banknote, ChevronLeft, ChevronRight,
    X, Tag, Layers, DollarSign, Calendar, Building2,
    RefreshCw, Sparkles, Pencil, FolderOpen, Rocket, PartyPopper,
    ChevronsLeft, ChevronsRight, BadgeCheck, ArrowUpRight, ArrowDownRight,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import React from 'react';

// ============================================================
// FORMATTERS
// ============================================================

const formatNaira = (val: string | number) => {
    const num = typeof val === 'string' ? parseFloat(val) : val;
    if (isNaN(num)) return '₦0.00';
    if (num >= 1000000) return `₦${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `₦${(num / 1000).toFixed(1)}K`;
    return `₦${num.toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;
};

const formatDate = (date: string) => {
    try { return new Date(date).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return 'N/A'; }
};

const formatTimeAgo = (date: string) => {
    try {
        const now = new Date();
        const then = new Date(date);
        const diffMs = now.getTime() - then.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return formatDate(date);
    } catch { return 'N/A'; }
};

// ============================================================
// HELPERS
// ============================================================

const extractErrorMessage = (err: any): string => {
    const apiError = err?.response?.data?.error;
    if (!apiError) return err?.message || 'An unexpected error occurred.';
    if (apiError.details && Array.isArray(apiError.details) && apiError.details.length > 0) {
        return apiError.details.map((d: any) => d.message).join('. ');
    }
    return apiError.message || 'An unexpected error occurred.';
};

const extractFieldErrors = (err: any): Record<string, string> => {
    const apiError = err?.response?.data?.error;
    if (!apiError?.details || !Array.isArray(apiError.details)) return {};
    const fieldErrors: Record<string, string> = {};
    apiError.details.forEach((detail: any) => {
        if (detail.field && detail.message) fieldErrors[detail.field] = detail.message;
    });
    return fieldErrors;
};

// ============================================================
// CELEBRATION OVERLAY
// ============================================================

function CelebrationOverlay({ show, message, onComplete }: { show: boolean; message: string; onComplete: () => void }) {
    useEffect(() => {
        if (show) { const timer = setTimeout(onComplete, 2500); return () => clearTimeout(timer); }
    }, [show, onComplete]);
    if (!show) return null;
    const colors = ['#F43F5E', '#F59E0B', '#06B6D4', '#8B5CF6', '#10B981', '#3B82F6', '#EC4899'];
    const pieces = Array.from({ length: 40 }, (_, i) => ({
        id: i, left: `${Math.random() * 100}%`, delay: Math.random() * 1.5,
        duration: 1.5 + Math.random() * 1.5, color: colors[Math.floor(Math.random() * colors.length)], size: 6 + Math.random() * 10
    }));
    return (
        <AnimatePresence>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] pointer-events-none overflow-hidden">
                {pieces.map(p => (
                    <motion.div key={p.id} initial={{ y: -20, opacity: 1 }}
                        animate={{ y: '100vh', x: `${(Math.random() - 0.5) * 150}px`, opacity: 0, rotate: 360 }}
                        transition={{ duration: p.duration, delay: p.delay, ease: 'easeIn' }}
                        className="absolute top-0 rounded-sm" style={{ left: p.left, width: p.size, height: p.size * 0.6, backgroundColor: p.color }} />
                ))}
                <motion.div initial={{ scale: 0, y: -50 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0, y: -50 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 15 }} className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-auto">
                    <div className="bg-white rounded-3xl shadow-2xl p-6 text-center border-2 border-slate-200">
                        <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 0.6, repeat: Infinity, repeatDelay: 0.5 }}
                            className="w-16 h-16 rounded-full bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center mx-auto mb-3 shadow-lg shadow-slate-200">
                            <Receipt size={32} className="text-white" />
                        </motion.div>
                        <h3 className="text-lg font-black text-slate-900">{message}</h3>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

// ============================================================
// STATUS CONFIG
// ============================================================

const statusConfig: Record<string, { variant: 'success' | 'warning' | 'danger' | 'neutral' | 'info'; icon: React.ElementType; label: string; gradient: string }> = {
    pending: { variant: 'warning', icon: Clock, label: 'Pending', gradient: 'from-amber-500 to-orange-600' },
    approved: { variant: 'info', icon: CheckCircle2, label: 'Approved', gradient: 'from-blue-500 to-indigo-600' },
    paid: { variant: 'success', icon: BadgeCheck, label: 'Paid', gradient: 'from-emerald-500 to-teal-600' },
    cancelled: { variant: 'danger', icon: XCircle, label: 'Cancelled', gradient: 'from-rose-500 to-pink-600' },
};

// ============================================================
// STAT CARD
// ============================================================

function StatCard({ label, value, icon: Icon, gradient, trend, sublabel }: {
    label: string; value: string | number; icon: React.ElementType; gradient: string; trend?: 'up' | 'down'; sublabel?: string;
}) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -3 }}
            className="relative overflow-hidden bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:shadow-lg transition-all group">
            <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl ${gradient} opacity-[0.06] rounded-bl-full group-hover:opacity-[0.12] transition-opacity`} />
            <div className="relative">
                <div className="flex items-start justify-between mb-3">
                    <div className={`p-2.5 rounded-xl bg-gradient-to-br ${gradient} shadow-md`}>
                        <Icon className="w-5 h-5 text-white" />
                    </div>
                    {trend && (
                        <span className={clsx('text-xs font-bold flex items-center gap-0.5', trend === 'up' ? 'text-rose-500' : 'text-emerald-500')}>
                            {trend === 'up' ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                        </span>
                    )}
                </div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{label}</p>
                <p className="text-xl font-bold text-slate-900 mt-1">
                    {typeof value === 'number' ? value.toLocaleString() : value}
                </p>
                {sublabel && <p className="text-[10px] text-slate-400 mt-0.5">{sublabel}</p>}
            </div>
        </motion.div>
    );
}

// ============================================================
// SKELETON
// ============================================================

function PageSkeleton() {
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
            <div className="max-w-[1600px] mx-auto p-4 lg:p-6 space-y-5">
                <div className="rounded-3xl bg-slate-200 animate-pulse h-40" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (<div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 animate-pulse h-28" />))}
                </div>
            </div>
        </div>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function ExpensesPage() {
    const navigate = useNavigate();
    const tenantId = useAuthStore(s => s.user?.tenantId);
    const queryClient = useQueryClient();
    const addToast = useToastStore(s => s.addToast);

    // Filters
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const limit = 15;

    // Expense form
    const [showExpenseForm, setShowExpenseForm] = useState(false);
    const [expenseForm, setExpenseForm] = useState({
        description: '', amount: '', categoryId: '', bankAccountId: '',
        paymentMethod: 'cash', // ✅ Default to cash
        referenceNumber: '', notes: '',
        payeeBankName: '', payeeAccountNumber: '', payeeAccountName: '',
    });
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    // Category form
    const [showCategoryForm, setShowCategoryForm] = useState(false);
    const [categoryForm, setCategoryForm] = useState({ name: '', description: '' });
    const [categoryFormErrors, setCategoryFormErrors] = useState<Record<string, string>>({});

    // Celebration
    const [showCelebration, setShowCelebration] = useState(false);
    const [celebrationMsg, setCelebrationMsg] = useState('');

    // Payment method options
    const paymentMethodOptions = [
        { value: 'cash', label: 'Cash' },
        { value: 'bank_transfer', label: 'Bank Transfer' },
        { value: 'card', label: 'Card' },
        { value: 'pos', label: 'POS' },
        { value: 'mobile_money', label: 'Mobile Money' },
        { value: 'cheque', label: 'Cheque' },
        { value: 'other', label: 'Other' },
    ];

    // ==================== QUERIES ====================

    const { data: expensesData, isLoading, isError, refetch } = useQuery({
        queryKey: ['expenses', tenantId, page, statusFilter, categoryFilter, search],
        queryFn: () => expensesApi.getExpenses({
            limit, page,
            status: statusFilter || undefined,
            categoryId: categoryFilter || undefined,
            search: search || undefined
        }),
        enabled: !!tenantId,
    });

    const { data: categories } = useQuery<ExpenseCategory[]>({
        queryKey: ['expense-categories', tenantId],
        queryFn: expensesApi.getExpenseCategories,
        enabled: !!tenantId,
    });

    const { data: accounts } = useQuery<BankAccount[]>({
        queryKey: ['bank-accounts', tenantId],
        queryFn: () => bankingApi.getBankAccounts(),
        enabled: !!tenantId,
    });

    // ==================== MUTATIONS ====================

    const createExpenseMutation = useMutation({
        mutationFn: () => expensesApi.createExpense({
            description: expenseForm.description.trim(),
            amount: parseFloat(expenseForm.amount),
            categoryId: expenseForm.categoryId,
            bankAccountId: expenseForm.bankAccountId,
            paymentMethod: expenseForm.paymentMethod || undefined,
            referenceNumber: expenseForm.referenceNumber || undefined,
            notes: expenseForm.notes || undefined,
            payeeBankName: expenseForm.payeeBankName || undefined,
            payeeAccountNumber: expenseForm.payeeAccountNumber || undefined,
            payeeAccountName: expenseForm.payeeAccountName || undefined,
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['expenses'] });
            queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
            queryClient.invalidateQueries({ queryKey: ['businesses'] });
            queryClient.invalidateQueries({ queryKey: ['businesses-stats'] });
            addToast({
                type: 'success',
                title: 'Expense Recorded! 📋',
                message: `${expenseForm.description}: ${formatNaira(expenseForm.amount)} has been recorded.`
            });
            setShowExpenseForm(false);
            setExpenseForm({ description: '', amount: '', categoryId: '', bankAccountId: '', paymentMethod: 'cash', referenceNumber: '', notes: '', payeeBankName: '', payeeAccountNumber: '', payeeAccountName: '' });
            setFormErrors({});
            setCelebrationMsg('Expense Recorded!');
            setShowCelebration(true);
        },
        onError: (err: any) => {
            const fe = extractFieldErrors(err);
            if (Object.keys(fe).length > 0) {
                setFormErrors(fe);
            } else {
                const msg = extractErrorMessage(err).toLowerCase();
                if (msg.includes('amount')) setFormErrors({ amount: extractErrorMessage(err) });
                else if (msg.includes('description')) setFormErrors({ description: extractErrorMessage(err) });
                else if (msg.includes('account') || msg.includes('bank')) setFormErrors({ bankAccountId: extractErrorMessage(err) });
                else if (msg.includes('category')) setFormErrors({ categoryId: extractErrorMessage(err) });
            }
            addToast({ type: 'error', title: 'Record Failed', message: extractErrorMessage(err) });
        },
    });

    const createCategoryMutation = useMutation({
        mutationFn: () => expensesApi.createExpenseCategory({
            name: categoryForm.name.trim(),
            description: categoryForm.description || undefined,
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['expense-categories'] });
            addToast({ type: 'success', title: 'Category Created 🏷️', message: `"${categoryForm.name}" has been added.` });
            setCategoryForm({ name: '', description: '' });
            setCategoryFormErrors({});
        },
        onError: (err: any) => {
            const fe = extractFieldErrors(err);
            if (Object.keys(fe).length > 0) setCategoryFormErrors(fe);
            addToast({ type: 'error', title: 'Category Failed', message: extractErrorMessage(err) });
        },
    });

    const approveMutation = useMutation({
        mutationFn: (id: string) => expensesApi.approveExpense(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['expenses'] });
            queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
            queryClient.invalidateQueries({ queryKey: ['businesses'] });
            queryClient.invalidateQueries({ queryKey: ['businesses-stats'] });
            addToast({ type: 'success', title: 'Approved & Paid ✅', message: 'Expense approved and account debited.' });
            setCelebrationMsg('Expense Approved!');
            setShowCelebration(true);
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Approval Failed', message: extractErrorMessage(err) }),
    });

    const cancelMutation = useMutation({
        mutationFn: (id: string) => expensesApi.cancelExpense(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['expenses'] });
            addToast({ type: 'success', title: 'Cancelled ❌', message: 'Expense has been cancelled.' });
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Cancel Failed', message: extractErrorMessage(err) }),
    });

    // ==================== HELPERS ====================

    const updateExpenseField = (field: string, value: string) => {
        setExpenseForm(p => ({ ...p, [field]: value }));
        if (formErrors[field]) {
            setFormErrors(p => { const u = { ...p }; delete u[field]; return u; });
        }
    };

    const updateCategoryField = (field: string, value: string) => {
        setCategoryForm(p => ({ ...p, [field]: value }));
        if (categoryFormErrors[field]) {
            setCategoryFormErrors(p => { const u = { ...p }; delete u[field]; return u; });
        }
    };

    const validateExpenseForm = () => {
        const errors: Record<string, string> = {};
        if (!expenseForm.description.trim()) errors.description = 'Description is required';
        if (!expenseForm.amount || parseFloat(expenseForm.amount) <= 0) errors.amount = 'Valid amount required';
        if (!expenseForm.categoryId) errors.categoryId = 'Category is required';
        if (!expenseForm.bankAccountId) errors.bankAccountId = 'Bank account is required';

        // ✅ New: Payee details required for non‑cash payment methods
        if (expenseForm.paymentMethod !== 'cash') {
            if (!expenseForm.payeeBankName.trim()) errors.payeeBankName = 'Bank name is required for digital payments';
            if (!expenseForm.payeeAccountNumber.trim()) errors.payeeAccountNumber = 'Account number is required for digital payments';
        }

        setFormErrors(errors);
        if (Object.keys(errors).length > 0) {
            addToast({ type: 'warning', title: 'Form Incomplete', message: 'Please fill all required fields.' });
            return false;
        }
        return true;
    };

    const validateCategoryForm = () => {
        const errors: Record<string, string> = {};
        if (!categoryForm.name.trim()) errors.name = 'Category name is required';
        setCategoryFormErrors(errors);
        if (Object.keys(errors).length > 0) {
            addToast({ type: 'warning', title: 'Name Required', message: 'Please enter a category name.' });
            return false;
        }
        return true;
    };

    // ==================== DERIVED DATA ====================

    const expenses: Expense[] = expensesData?.expenses || [];
    const totalCount = expensesData?.totalCount || 0;
    const totalPages = Math.ceil(totalCount / limit);
    const totalAmount = expenses.reduce((s, e) => s + parseFloat(e.amount || '0'), 0);
    const pendingAmount = expenses.filter(e => e.status === 'pending').reduce((s, e) => s + parseFloat(e.amount || '0'), 0);
    const paidAmount = expenses.filter(e => e.status === 'paid').reduce((s, e) => s + parseFloat(e.amount || '0'), 0);
    const pendingCount = expenses.filter(e => e.status === 'pending').length;

    // ==================== LOADING / ERROR ====================

    if (isLoading) return <PageSkeleton />;

    if (isError) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6">
                <ErrorState title="Failed to load expenses" message="There was a problem loading your expenses." onRetry={() => refetch()} />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
            <div className="max-w-[1600px] mx-auto p-4 lg:p-6 space-y-5">

                <CelebrationOverlay show={showCelebration} message={celebrationMsg} onComplete={() => setShowCelebration(false)} />

                {/* ========== HEADER ========== */}
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-600 mb-3 group transition-colors">
                        <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
                        Back to Dashboard
                    </button>

                    <div className="relative overflow-hidden bg-gradient-to-br from-teal-500 via-emerald-600 to-teal-700 rounded-3xl shadow-2xl">
                        <div className="absolute inset-0">
                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.15),transparent_50%)]" />
                            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
                        </div>
                        <div className="relative px-6 lg:px-10 py-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                            <div className="flex items-center gap-4">
                                <motion.div
                                    whileHover={{ rotate: -5, scale: 1.05 }}
                                    className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur-sm ring-1 ring-white/20 flex items-center justify-center shadow-xl">
                                    <Receipt size={32} className="text-white" />
                                </motion.div>
                                <div>
                                    <h1 className="text-3xl lg:text-4xl font-black text-white tracking-tight">Expenses</h1>
                                    <p className="text-teal-100 text-sm mt-1 font-medium">
                                        {totalCount} expense{totalCount !== 1 ? 's' : ''} · {pendingCount} pending · Total: <span className="font-bold text-white">{formatNaira(totalAmount)}</span>
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <Button
                                    size="md"
                                    variant="secondary"
                                    className="!bg-white/15 !text-white !border-white/20 hover:!bg-white/25 !font-medium !rounded-2xl"
                                    leftIcon={<FolderOpen size={16} />}
                                    onClick={() => setShowCategoryForm(true)}>
                                    Categories
                                </Button>
                                <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                                    <Button
                                        size="md"
                                        className="!bg-white !text-emerald-700 hover:!text-emerald-800 !font-bold !px-6 !py-3 !rounded-2xl !text-base !border-2 !border-white/80 !shadow-xl !shadow-black/20 hover:!shadow-black/30 hover:!bg-emerald-50 transition-all duration-300"
                                        leftIcon={<Plus size={18} />}
                                        onClick={() => setShowExpenseForm(true)}>
                                        Record Expense
                                    </Button>
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* ========== STATS ========== */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard label="Total Expenses" value={formatNaira(totalAmount)} icon={DollarSign} gradient="from-slate-500 to-slate-700" trend="up" sublabel={`${totalCount} records`} />
                    <StatCard label="Pending" value={formatNaira(pendingAmount)} icon={Clock} gradient="from-amber-500 to-orange-600" sublabel={`${pendingCount} awaiting`} />
                    <StatCard label="Paid" value={formatNaira(paidAmount)} icon={BadgeCheck} gradient="from-emerald-500 to-teal-600" trend="down" />
                    <StatCard label="Categories" value={categories?.length || 0} icon={Tag} gradient="from-purple-500 to-violet-600" />
                </div>

                {/* ========== CATEGORIES QUICK VIEW ========== */}
                {categories && categories.length > 0 && (
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                                <FolderOpen size={16} className="text-slate-500" />
                                Expense Categories
                            </h3>
                            <button
                                onClick={() => setShowCategoryForm(true)}
                                className="text-xs text-slate-500 hover:text-slate-700 font-medium flex items-center gap-1">
                                <Plus size={12} /> Add Category
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {categories.map(cat => (
                                <button
                                    key={cat.id}
                                    onClick={() => setCategoryFilter(categoryFilter === cat.id ? '' : cat.id)}
                                    className={clsx(
                                        'px-3 py-1.5 rounded-xl text-xs font-medium transition-all border',
                                        categoryFilter === cat.id
                                            ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                                            : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-800'
                                    )}>
                                    {cat.name}
                                    {cat.isSystem && <span className="ml-1 text-[9px] opacity-60">· system</span>}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* ========== FILTERS ========== */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm sticky top-4 z-30 backdrop-blur-xl bg-white/95 p-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search expenses..."
                                value={search}
                                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                                className="pl-9 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm w-48 focus:ring-2 focus:ring-slate-500/20 focus:border-slate-400 focus:bg-white transition-all"
                            />
                            {search && (
                                <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2">
                                    <X size={12} className="text-slate-400 hover:text-slate-600" />
                                </button>
                            )}
                        </div>
                        <div className="flex gap-1 flex-wrap">
                            {['', 'pending', 'paid', 'cancelled'].map(status => (
                                <button
                                    key={status}
                                    onClick={() => { setStatusFilter(status); setPage(1); }}
                                    className={clsx(
                                        'px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                                        statusFilter === status
                                            ? 'bg-emerald-600 text-white shadow-sm'
                                            : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                                    )}>
                                    {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'All'}
                                </button>
                            ))}
                        </div>
                        {(statusFilter || categoryFilter || search) && (
                            <button
                                onClick={() => { setStatusFilter(''); setCategoryFilter(''); setSearch(''); setPage(1); }}
                                className="text-xs text-slate-500 hover:text-slate-700 font-medium flex items-center gap-1">
                                <X size={12} /> Clear
                            </button>
                        )}
                    </div>
                    <button
                        onClick={() => refetch()}
                        className="p-2 rounded-lg hover:bg-slate-50 text-slate-400 transition-colors"
                        title="Refresh">
                        <RefreshCw size={14} />
                    </button>
                </div>

                {/* ========== EXPENSES TABLE ========== */}
                {expenses.length === 0 ? (
                    <EmptyState
                        icon={<Receipt size={48} />}
                        title="No expenses"
                        description={search || statusFilter || categoryFilter ? 'No expenses match your filters.' : 'Record your business expenses here.'}
                        action={!search && !statusFilter && !categoryFilter ? { label: 'Record Expense', onClick: () => setShowExpenseForm(true) } : { label: 'Clear Filters', onClick: () => { setStatusFilter(''); setCategoryFilter(''); setSearch(''); } }}
                    />
                ) : (
                    <>
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-100">
                                    <thead>
                                        <tr className="bg-slate-50">
                                            <th className="px-6 py-4 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Description</th>
                                            <th className="px-6 py-4 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Category</th>
                                            <th className="px-6 py-4 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider">Amount</th>
                                            <th className="px-6 py-4 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider">Status</th>
                                            <th className="px-6 py-4 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden md:table-cell">Account</th>
                                            <th className="px-6 py-4 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Date</th>
                                            <th className="px-6 py-4 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {expenses.map((expense) => {
                                            const config = statusConfig[expense.status] || statusConfig.pending;
                                            const StatusIcon = config.icon;
                                            return (
                                                <motion.tr
                                                    key={expense.id}
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    className="hover:bg-slate-50 transition-colors group">
                                                    <td className="px-6 py-4">
                                                        <p className="text-sm font-medium text-slate-900">{expense.description}</p>
                                                        {expense.referenceNumber && (
                                                            <p className="text-xs text-slate-400 mt-0.5 font-mono">Ref: {expense.referenceNumber}</p>
                                                        )}
                                                        {expense.paymentMethod && (
                                                            <p className="text-xs text-slate-400 capitalize">{expense.paymentMethod.replace('_', ' ')}</p>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <Badge variant="neutral" size="sm">
                                                            {expense.categoryName || 'Uncategorized'}
                                                        </Badge>
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <span className="text-sm font-bold text-slate-900">{formatNaira(expense.amount)}</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <Badge variant={config.variant} size="sm">
                                                            <StatusIcon size={10} className="mr-1" />{config.label}
                                                        </Badge>
                                                    </td>
                                                    <td className="px-6 py-4 hidden md:table-cell">
                                                        <span className="text-xs text-slate-500">{expense.bankAccountName || '-'}</span>
                                                    </td>
                                                    <td className="px-6 py-4 hidden lg:table-cell">
                                                        <span className="text-xs text-slate-500">{formatTimeAgo(expense.expenseDate)}</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-all">
                                                            {expense.status === 'pending' && (
                                                                <>
                                                                    <button
                                                                        onClick={() => {
                                                                            if (window.confirm(`Approve and pay this expense of ${formatNaira(expense.amount)}? This will debit the account.`))
                                                                                approveMutation.mutate(expense.id);
                                                                        }}
                                                                        className="p-1.5 rounded-lg hover:bg-emerald-50 text-slate-400 hover:text-emerald-600 transition-all"
                                                                        title="Approve & Pay">
                                                                        <CheckCircle2 size={14} />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => {
                                                                            if (window.confirm('Cancel this expense?'))
                                                                                cancelMutation.mutate(expense.id);
                                                                        }}
                                                                        className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-500 transition-all"
                                                                        title="Cancel Expense">
                                                                        <XCircle size={14} />
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                </motion.tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* ========== PAGINATION (Business Page Style) ========== */}
                        {totalPages > 1 && (
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                                <div className="px-6 py-4 bg-slate-50/50 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
                                    <p className="text-sm text-slate-600">
                                        Showing <span className="font-semibold text-slate-900">{(page - 1) * limit + 1}</span> to{' '}
                                        <span className="font-semibold text-slate-900">{Math.min(page * limit, totalCount)}</span> of{' '}
                                        <span className="font-semibold text-slate-900">{totalCount.toLocaleString()}</span>
                                    </p>
                                    <div className="flex gap-2 flex-wrap">
                                        <button onClick={() => setPage(1)} disabled={page === 1} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 disabled:opacity-30" type="button">
                                            <ChevronsLeft className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-4 py-2 border border-slate-200 rounded-xl text-sm disabled:opacity-40 hover:bg-slate-100 font-medium transition flex items-center gap-1" type="button">
                                            <ChevronLeft size={14} /> Prev
                                        </button>
                                        {(() => {
                                            const pageNumbers: (number | 'ellipsis')[] = [];
                                            if (totalPages <= 7) {
                                                for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
                                            } else {
                                                pageNumbers.push(1);
                                                if (page > 3) pageNumbers.push('ellipsis');
                                                for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pageNumbers.push(i);
                                                if (page < totalPages - 2) pageNumbers.push('ellipsis');
                                                pageNumbers.push(totalPages);
                                            }
                                            return pageNumbers.map((p, idx) => (
                                                <React.Fragment key={idx}>
                                                    {p === 'ellipsis' ? (
                                                        <span className="px-2 py-2 text-slate-400">...</span>
                                                    ) : (
                                                        <button
                                                            onClick={() => setPage(p as number)}
                                                            className={clsx(
                                                                'px-4 py-2 rounded-xl text-sm font-medium transition',
                                                                page === p
                                                                    ? 'bg-gradient-to-r from-slate-700 to-slate-800 text-white shadow-md shadow-slate-500/25'
                                                                    : 'border border-slate-200 hover:bg-slate-100 text-slate-700'
                                                            )}
                                                            type="button">
                                                            {p}
                                                        </button>
                                                    )}
                                                </React.Fragment>
                                            ));
                                        })()}
                                        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="px-4 py-2 border border-slate-200 rounded-xl text-sm disabled:opacity-40 hover:bg-slate-100 font-medium transition flex items-center gap-1" type="button">
                                            Next <ChevronRight size={14} />
                                        </button>
                                        <button onClick={() => setPage(totalPages)} disabled={page >= totalPages} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 disabled:opacity-30" type="button">
                                            <ChevronsRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* ========== RECORD EXPENSE MODAL (ENHANCED) ========== */}
                <AnimatePresence>
                    {showExpenseForm && (
                        <motion.div
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                            onClick={() => setShowExpenseForm(false)}>
                            <motion.div
                                initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
                                onClick={(e) => e.stopPropagation()}
                                className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">

                                {/* Modal Header */}
                                <div className="sticky top-0 bg-white rounded-t-3xl z-10">
                                    <div className="px-6 py-4 bg-gradient-to-r from-emerald-600 to-teal-700 rounded-t-3xl">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 rounded-xl bg-white/20">
                                                    <Receipt size={20} className="text-white" />
                                                </div>
                                                <div>
                                                    <h2 className="text-lg font-bold text-white">Record Expense</h2>
                                                    <p className="text-teal-100 text-xs">Enter expense details</p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => setShowExpenseForm(false)}
                                                className="p-2 rounded-xl bg-white/20 text-white hover:bg-white/30 transition-colors">
                                                <X size={18} />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Modal Body – Two-column grid for better UX */}
                                <div className="p-6 space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <Input
                                            label="Description *"
                                            value={expenseForm.description}
                                            error={formErrors.description}
                                            onChange={(e) => updateExpenseField('description', e.target.value)}
                                            placeholder="e.g., Office supplies for Q4"
                                            required
                                        />
                                        <Input
                                            label="Amount *"
                                            type="number"
                                            value={expenseForm.amount}
                                            error={formErrors.amount}
                                            onChange={(e) => updateExpenseField('amount', e.target.value)}
                                            placeholder="0.00"
                                            required
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                                Category *
                                            </label>
                                            <div className="flex gap-2">
                                                <select
                                                    value={expenseForm.categoryId}
                                                    onChange={(e) => updateExpenseField('categoryId', e.target.value)}
                                                    className={clsx(
                                                        'flex-1 px-4 py-2.5 border-2 rounded-xl text-sm bg-white focus:ring-2 focus:ring-slate-500/20 focus:border-slate-400',
                                                        formErrors.categoryId ? 'border-rose-300' : 'border-slate-200'
                                                    )}>
                                                    <option value="">Select category</option>
                                                    {(categories || []).map(c => (
                                                        <option key={c.id} value={c.id}>{c.name}</option>
                                                    ))}
                                                </select>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowCategoryForm(true)}
                                                    className="px-3 py-2.5 border-2 border-slate-200 rounded-xl hover:bg-slate-50 text-slate-500 transition-colors"
                                                    title="Add Category">
                                                    <Plus size={16} />
                                                </button>
                                            </div>
                                            {formErrors.categoryId && (
                                                <p className="text-xs text-rose-500 mt-1">{formErrors.categoryId}</p>
                                            )}
                                        </div>

                                        {/* ✅ Corrected: Native select for bank account */}
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                                Bank Account *
                                            </label>
                                            <select
                                                value={expenseForm.bankAccountId}
                                                onChange={(e) => updateExpenseField('bankAccountId', e.target.value)}
                                                className={clsx(
                                                    'w-full px-4 py-2.5 border-2 rounded-xl text-sm bg-white focus:ring-2 focus:ring-slate-500/20 focus:border-slate-400',
                                                    formErrors.bankAccountId ? 'border-rose-300' : 'border-slate-200'
                                                )}>
                                                <option value="">Select account</option>
                                                {(accounts || []).map(a => (
                                                    <option key={a.id} value={a.id}>
                                                        {a.bankName} - {a.accountNumber} ({formatNaira(a.currentBalance)})
                                                    </option>
                                                ))}
                                            </select>
                                            {formErrors.bankAccountId && (
                                                <p className="text-xs text-rose-500 mt-1">{formErrors.bankAccountId}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                                Payment Method *
                                            </label>
                                            <select
                                                value={expenseForm.paymentMethod}
                                                onChange={(e) => updateExpenseField('paymentMethod', e.target.value)}
                                                className={clsx(
                                                    'w-full px-4 py-2.5 border-2 rounded-xl text-sm bg-white focus:ring-2 focus:ring-slate-500/20 focus:border-slate-400',
                                                    formErrors.paymentMethod ? 'border-rose-300' : 'border-slate-200'
                                                )}>
                                                {paymentMethodOptions.map(opt => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <Input
                                            label="Reference Number"
                                            value={expenseForm.referenceNumber}
                                            onChange={(e) => updateExpenseField('referenceNumber', e.target.value)}
                                            placeholder="Optional reference"
                                        />
                                    </div>

                                    {/* ✅ Payee Details – shown for all non‑cash methods in a balanced grid */}
                                    {expenseForm.paymentMethod !== 'cash' && (
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 bg-emerald-50/50 rounded-2xl border border-emerald-100">
                                            <Input
                                                label="Payee Bank Name *"
                                                value={expenseForm.payeeBankName}
                                                error={formErrors.payeeBankName}
                                                onChange={(e) => updateExpenseField('payeeBankName', e.target.value)}
                                                placeholder="e.g., Access Bank"
                                                required
                                            />
                                            <Input
                                                label="Payee Account Number *"
                                                value={expenseForm.payeeAccountNumber}
                                                error={formErrors.payeeAccountNumber}
                                                onChange={(e) => updateExpenseField('payeeAccountNumber', e.target.value)}
                                                placeholder="10-digit NUBAN"
                                                required
                                            />
                                            <Input
                                                label="Payee Account Name"
                                                value={expenseForm.payeeAccountName}
                                                onChange={(e) => updateExpenseField('payeeAccountName', e.target.value)}
                                                placeholder="Account holder name"
                                            />
                                        </div>
                                    )}

                                    <Input
                                        label="Notes"
                                        value={expenseForm.notes}
                                        onChange={(e) => updateExpenseField('notes', e.target.value)}
                                        placeholder="Any additional notes..."
                                    />
                                </div>

                                {/* Footer */}
                                <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 rounded-b-3xl px-6 py-4 flex justify-end gap-3">
                                    <Button variant="secondary" onClick={() => setShowExpenseForm(false)}>Cancel</Button>
                                    <Button
                                        onClick={() => { if (validateExpenseForm()) createExpenseMutation.mutate(); }}
                                        isLoading={createExpenseMutation.isPending}
                                        disabled={!expenseForm.description || !expenseForm.amount || !expenseForm.categoryId || !expenseForm.bankAccountId}
                                        leftIcon={<Rocket size={16} />}>
                                        Record Expense
                                    </Button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* ========== CREATE CATEGORY MODAL ========== */}
                <AnimatePresence>
                    {showCategoryForm && (
                        <motion.div
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                            onClick={() => setShowCategoryForm(false)}>
                            <motion.div
                                initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
                                onClick={(e) => e.stopPropagation()}
                                className="bg-white rounded-3xl shadow-2xl w-full max-w-md">

                                <div className="sticky top-0 bg-white rounded-t-3xl z-10">
                                    <div className="px-8 py-5 bg-gradient-to-r from-emerald-600 to-teal-700 rounded-t-3xl">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 rounded-xl bg-white/20">
                                                    <Tag size={20} className="text-white" />
                                                </div>
                                                <div>
                                                    <h2 className="text-lg font-bold text-white">Expense Categories</h2>
                                                    <p className="text-teal-100 text-xs">Manage expense categories</p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => setShowCategoryForm(false)}
                                                className="p-2 rounded-xl bg-white/20 text-white hover:bg-white/30 transition-colors">
                                                <X size={18} />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-8 space-y-4">
                                    {categories && categories.length > 0 && (
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">
                                                Existing Categories ({categories.length})
                                            </label>
                                            <div className="space-y-1 max-h-48 overflow-y-auto">
                                                {categories.map(cat => (
                                                    <div
                                                        key={cat.id}
                                                        className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg">
                                                        <div className="flex items-center gap-2">
                                                            <Tag size={12} className="text-slate-400" />
                                                            <span className="text-sm text-slate-700">{cat.name}</span>
                                                            {cat.isSystem && (
                                                                <Badge variant="neutral" size="sm">System</Badge>
                                                            )}
                                                        </div>
                                                        {cat.description && (
                                                            <span className="text-[10px] text-slate-400 truncate max-w-[150px]">
                                                                {cat.description}
                                                            </span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="border-t border-slate-200 pt-4">
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">
                                            Add New Category
                                        </label>
                                        <Input
                                            label="Category Name *"
                                            value={categoryForm.name}
                                            error={categoryFormErrors.name}
                                            onChange={(e) => updateCategoryField('name', e.target.value)}
                                            placeholder="e.g., Software Subscriptions"
                                            required
                                        />
                                        <div className="mt-3">
                                            <Input
                                                label="Description (optional)"
                                                value={categoryForm.description}
                                                onChange={(e) => updateCategoryField('description', e.target.value)}
                                                placeholder="Brief description of this category"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 rounded-b-3xl px-8 py-4 flex justify-end gap-3">
                                    <Button variant="secondary" onClick={() => setShowCategoryForm(false)}>Close</Button>
                                    <Button
                                        onClick={() => { if (validateCategoryForm()) createCategoryMutation.mutate(); }}
                                        isLoading={createCategoryMutation.isPending}
                                        disabled={!categoryForm.name.trim()}
                                        leftIcon={<Plus size={14} />}>
                                        Add Category
                                    </Button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}